// Shared pricing math. Pure functions, no I/O. Used by the server (checkout
// endpoint, webhook validation) and the client (checkout-page breakdown).

export type CouponDiscount =
  | { type: 'percentage'; value: number } // 0..100
  | { type: 'fixed'; value: number };     // cents

export type PriceBreakdown = {
  basePriceCents: number;
  wasLaunchPrice: boolean;
  discountCents: number;
  totalCents: number;
};

// Per-course base price selection. Owner accepted small race risk on launch
// seats (07-payments-and-invoicing.md) so this just compares the in-memory
// counters; the webhook handler does the race-safe DB increment.
export function selectBasePrice(args: {
  priceCents: number;
  launchPriceCents: number | null;
  launchSeatsTotal: number;
  launchSeatsUsed: number;
}): { priceCents: number; wasLaunchPrice: boolean } {
  const { priceCents, launchPriceCents, launchSeatsTotal, launchSeatsUsed } = args;
  if (launchPriceCents != null && launchSeatsUsed < launchSeatsTotal) {
    return { priceCents: launchPriceCents, wasLaunchPrice: true };
  }
  return { priceCents, wasLaunchPrice: false };
}

export function applyCoupon(amountCents: number, coupon: CouponDiscount): number {
  if (amountCents <= 0) return 0;
  if (coupon.type === 'percentage') {
    const pct = clamp(coupon.value, 0, 100);
    return Math.max(0, Math.round(amountCents * (1 - pct / 100)));
  }
  return Math.max(0, amountCents - Math.max(0, coupon.value));
}

export function calculateFinalPrice(args: {
  priceCents: number;
  launchPriceCents: number | null;
  launchSeatsTotal: number;
  launchSeatsUsed: number;
  coupon: CouponDiscount | null;
}): PriceBreakdown {
  const { priceCents: base, wasLaunchPrice } = selectBasePrice(args);
  if (!args.coupon) {
    return { basePriceCents: base, wasLaunchPrice, discountCents: 0, totalCents: base };
  }
  const total = applyCoupon(base, args.coupon);
  return {
    basePriceCents: base,
    wasLaunchPrice,
    discountCents: base - total,
    totalCents: total,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
